import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight } from "@phosphor-icons/react";
import imgHive from "../../../../images/work/work-hive.webp";
import imgMk2 from "../../../../images/work/work-mk2.webp";
import imgAtmos from "../../../../images/work/work-atmos.webp";
import imgVsc from "../../../../images/work/work-vsc.webp";
import imgHood from "../../../../images/work/work-portablehood.webp";
import imgAdditional from "../../../../images/work/work-additional.webp";
import "./projects.scss";

gsap.registerPlugin(ScrollTrigger);

// Every entry maps to a case study that actually exists, so no card is a
// dead end. Descriptions are drawn from each deck's own content.
const PROJECTS = [
  {
    to: "/hive",
    title: "HIVE",
    blurb: "Filter-less air purification, now shipping on Amazon",
    img: imgHive,
    alt: "HIVE air purifier standing beside a bed in a bedroom",
  },
  {
    to: "/mk2",
    title: "MK2 Application",
    blurb: "Device management interface for connected purifiers",
    img: imgMk2,
    alt: "MK2 application screens arranged around a laptop",
  },
  {
    to: "/atmos",
    title: "ATMOS",
    blurb: "Steam cleaning device for gear and equipment",
    img: imgAtmos,
    alt: "ATMOS steam cleaning device detail render",
  },
  {
    to: "/vsc",
    title: "VSC",
    blurb: "Vacuum sealing and steam cooking appliance",
    img: imgVsc,
    alt: "VSC appliance steam cooking salmon and vegetables",
  },
  {
    to: "/portablehood",
    title: "Portable Hood",
    blurb: "Portable kitchen extraction hood",
    img: imgHood,
    alt: "Portable extraction hood product render",
  },
  {
    to: "/additionalproject",
    title: "Additional Work",
    blurb: "Sketching, CAD and visualisation studies",
    img: imgAdditional,
    alt: "Speaker concept visualisation",
  },
];

const Projects = () => {
  const sectionRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const section = sectionRef.current;
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!section || !viewport || !track) return;

    const mm = gsap.matchMedia();

    // Desktop and tablet only, and only when motion is welcome. Everywhere
    // else the same markup is a native scroll-snap carousel (see the
    // stylesheet), which needs no JavaScript at all.
    mm.add(
      "(min-width: 768px) and (prefers-reduced-motion: no-preference)",
      () => {
        const cards = cardsRef.current.filter(Boolean);
        const n = cards.length;
        if (n < 2) return;

        let stride = 0;
        let centerOffset = 0;

        const measure = () => {
          stride = cards[1].offsetLeft - cards[0].offsetLeft;
          centerOffset = (viewport.clientWidth - cards[0].offsetWidth) / 2;
        };
        measure();

        const setX = gsap.quickSetter(track, "x", "px");
        const setters = cards.map((c) => ({
          rotY: gsap.quickSetter(c, "rotationY", "deg"),
          z: gsap.quickSetter(c, "z", "px"),
          opacity: gsap.quickSetter(c, "opacity"),
        }));

        const render = (progress) => {
          // Position along the track, in card units.
          const center = progress * (n - 1);
          setX(centerOffset - center * stride);

          cards.forEach((card, i) => {
            const d = i - center;
            const ad = Math.abs(d);
            // Cards turn away from the camera as they leave centre and sink
            // backwards, so the row reads as depth rather than as a slide.
            setters[i].rotY(gsap.utils.clamp(-34, 34, -d * 15));
            setters[i].z(-Math.min(ad, 3) * 240);
            setters[i].opacity(gsap.utils.clamp(0.18, 1, 1 - ad * 0.34));
            card.style.zIndex = String(Math.round(100 - ad * 10));
          });
        };

        const trigger = ScrollTrigger.create({
          trigger: section,
          start: "top top",
          end: () => `+=${window.innerHeight * (n - 1) * 0.5}`,
          pin: true,
          scrub: 0.45,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onRefresh: (self) => {
            measure();
            render(self.progress);
          },
          onUpdate: (self) => render(self.progress),
        });

        render(0);

        return () => {
          trigger.kill();
          gsap.set([track, ...cards], { clearProps: "all" });
        };
      }
    );

    return () => mm.revert();
  }, []);

  return (
    <section
      className="work"
      id="work"
      ref={sectionRef}
      aria-labelledby="work-heading"
    >
      <div className="work__head">
        <h2 className="work__heading" id="work-heading">
          Selected work
        </h2>
      </div>

      <div className="work__viewport" ref={viewportRef}>
        <ul className="work__track" ref={trackRef}>
          {PROJECTS.map((p, i) => (
            <li
              className="work__card"
              key={p.to}
              ref={(el) => (cardsRef.current[i] = el)}
            >
              <Link to={p.to} className="work__link">
                <div className="work__media">
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading={i < 2 ? "eager" : "lazy"}
                    decoding="async"
                  />
                </div>
                <div className="work__meta">
                  <h3 className="work__title">{p.title}</h3>
                  <p className="work__blurb">{p.blurb}</p>
                  <span className="work__cue" aria-hidden="true">
                    <ArrowUpRight size={18} weight="bold" />
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Projects;
